<template>
    <app-modal :modal-id="modalId" :preloader="preloader" @close-modal="closeModal" modal-size="default">
        <template slot="header">
            <h5 class="modal-title">
                {{ `${$t('device_assignment')} - ${tripData.lot_number || ''}${tripData.horse ? ` / ${tripData.horse}` : ''}` }}
            </h5>
            <button type="button" class="close outline-none" @click.prevent="closeModal">
                <span class="mb-1">
                    <app-icon :name="'x'"></app-icon>
                </span>
            </button>
        </template>
        <template slot="body">
            <app-overlay-loader v-if="preloader" />
            <div :class="{ 'loading-opacity': preloader }">
                <div class="p-2 mb-3" style="background-color: #F1F5FA;">
                    <h6 class="mb-0">{{ $t('assigned_devices') }}</h6>
                </div>

                <div v-if="activeDevices.length > 0" class="mb-4">
                    <div v-for="device in activeDevices" :key="device.id" class="border rounded p-3 mb-2">
                        <div class="d-flex justify-content-between align-items-start">
                            <div>
                                <div class="font-weight-bold">{{ device.imei || '---' }}</div>
                                <div class="text-muted text-size-13" v-if="device.name">{{ device.name }}</div>
                                <div class="text-muted text-size-13" v-if="device.assigned_at">
                                    {{ `${$t('assigned_at')}: ${formatAssignedAt(device.assigned_at)}` }}
                                </div>
                            </div>
                            <button v-if="canUnassignDevices" type="button" class="btn btn-outline-danger btn-sm"
                                :disabled="unassigningDeviceId === device.id"
                                @click.prevent="unassignDevice(device.id)">
                                <span v-if="unassigningDeviceId === device.id">
                                    <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                    {{ `${$t('loading')}...` }}
                                </span>
                                <span v-else>{{ $t('unassign_device') }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-else class="alert alert-light border text-muted mb-4">
                    {{ $t('no_device_assigned') }}
                </div>

                <div v-if="canAssignDevices" class="p-2 mb-3" style="background-color: #F1F5FA;">
                    <h6 class="mb-0">{{ $t('assign_device') }}</h6>
                </div>

                <div v-if="canAssignDevices" class="form-group mb-0">
                    <label for="trip-device-select">{{ $t('device') }}</label>
                    <app-input id="trip-device-select" class="w-100" v-model="inputs.device_id" :list="availableDeviceOptions"
                        list-value-field="label" :placeholder="$t('select_device')" type="search-select" />
                    <small v-if="availableDeviceOptions.length === 0" class="text-muted d-block mt-2">
                        {{ $t('no_available_device') }}
                    </small>
                </div>
            </div>
        </template>
        <template slot="footer">
            <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal" @click="closeModal">
                {{ $t('cancel') }}
            </button>
            <button v-if="canAssignDevices" type="button" class="btn btn-primary" :disabled="submitLoader || !inputs.device_id || availableDeviceOptions.length === 0"
                @click="assignDevice">
                <span v-if="submitLoader">
                    <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ `${$t('loading')}...` }}
                </span>
                <span v-else>{{ $t('assign_device') }}</span>
            </button>
        </template>
    </app-modal>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import * as actions from '../../../Config/ApiUrl';
import { formatDateToLocal } from '../../../Helpers/Helpers';
import { ModalMixin } from '../../../Mixins/ModalMixin';

export default {
    name: 'TripDeviceAssignmentModal',
    extends: CoreLibrary,
    mixins: [FormMixin, ModalMixin],
    props: {
        tableId: {
            type: String,
            required: true,
        },
        tripData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            modalId: 'trip-device-assignment-modal',
            preloader: false,
            submitLoader: false,
            unassigningDeviceId: null,
            activeDevices: [],
            availableDevices: [],
            inputs: {
                device_id: null,
            },
        };
    },
    computed: {
        canAssignDevices() {
            return this.$can('assign_device_trips');
        },
        canUnassignDevices() {
            return this.$can('unassign_device_trips');
        },
        canManageDevices() {
            return this.canAssignDevices || this.canUnassignDevices;
        },
        availableDeviceOptions() {
            return this.availableDevices.map((device) => ({
                ...device,
                label: device.imei
                    ? `${device.imei}${device.name ? ` - ${device.name}` : ''}`
                    : (device.name || ''),
            }));
        },
    },
    methods: {
        formatAssignedAt(value) {
            return formatDateToLocal(value, true);
        },
        fetchDevices(showLoader = true) {
            if (showLoader) {
                this.preloader = true;
            }

            this.axiosGet(`${actions.TRIP}/${this.tripData.id}/devices`)
                .then((response) => {
                    this.activeDevices = response.data.active_devices || [];
                    this.availableDevices = response.data.available_devices || [];
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : this.$t('failed_response');

                    this.$toastr.e(message);
                    this.closeModal();
                })
                .finally(() => {
                    if (showLoader) {
                        this.preloader = false;
                    }
                });
        },
        assignDevice() {
            if (!this.inputs.device_id) {
                return;
            }

            this.submitLoader = true;

            this.axiosPost({
                url: `${actions.TRIP}/${this.tripData.id}/devices`,
                data: this.inputs,
            }).then((response) => {
                this.$toastr.s(response.data.message);
                this.inputs.device_id = null;
                this.$hub.$emit('reload-' + this.tableId);
                this.fetchDevices(false);
            }).catch((error) => {
                const message = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : this.$t('failed_response');

                this.$toastr.e(message);
            }).finally(() => {
                this.submitLoader = false;
            });
        },
        unassignDevice(deviceId) {
            this.unassigningDeviceId = deviceId;

            this.axiosDelete(`${actions.TRIP}/${this.tripData.id}/devices/${deviceId}`)
                .then((response) => {
                    this.$toastr.s(response.data.message);
                    this.$hub.$emit('reload-' + this.tableId);
                    this.fetchDevices(false);
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : this.$t('failed_response');

                    this.$toastr.e(message);
                })
                .finally(() => {
                    this.unassigningDeviceId = null;
                });
        },
    },
    mounted() {
        if (!this.canManageDevices) {
            this.$toastr.e(this.$t('forbidden_error_message'));
            this.closeModal();
            return;
        }

        this.fetchDevices();
    },
}
</script>
